import React, { useState, useEffect } from "react";
import axiosClient from "../../utilities/axios_client";
import {
  Button,
  Link,
  Table,
  TableBody,
  TableRow,
  TableCell,
  LoadingMessage,
  Card,
  BackLink,
} from "../../groundworkui-ui";
import DeploymentEnvironmentsFormModal from "./deployment_environments_form/deployment_environments_form_modal";
import { rootUrl } from "../../utilities/general_helpers";

const DeploymentEnvironmentsIndex = ({ projectId }) => {
  const [loading, setLoading] = useState(true);
  const [deploymentEnvironments, setDeploymentEnvironments] = useState([]);
  const [
    showDeploymentEnvironmentFormModal,
    setShowDeploymentEnvironmentFormModal,
  ] = useState(false);

  const loadDeploymentEnvironments = () => {
    axiosClient
      .get(`/projects/${projectId}/deployment_environments`)
      .then((response) => {
        setDeploymentEnvironments(response.data);
        setLoading(false);
      });
  };

  const publish = (deploymentEnvironment) => {
    if (
      window.confirm(
        `Are you sure you want to publish the latest version to '${deploymentEnvironment.name}'?`,
      )
    ) {
      axiosClient
        .put(
          `/projects/${projectId}/deployment_environments/${deploymentEnvironment.id}/publish`,
        )
        .then(() => {
          window.alert("Published!");
          loadDeploymentEnvironments();
        });
    }
  };

  useEffect(() => {
    loadDeploymentEnvironments();
  }, []);

  if (loading) {
    return <LoadingMessage message="Loading deployment environments..." />;
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        <div>
          <BackLink href={`/projects/${projectId}`} />
          <h1>Deployment Environments</h1>
        </div>

        {deploymentEnvironments.length === 0 ? (
          <Card type="empty-state">
            You do not have any deployment environments setup.
          </Card>
        ) : (
          <Table>
            <TableBody>
              {deploymentEnvironments.map((deploymentEnvironment) => {
                return (
                  <TableRow
                    className="w-full h-min"
                    key={deploymentEnvironment.id}
                  >
                    <TableCell>
                      <div className="flex flex-col gap-2">
                        <div>
                          <div className="font-bold">
                            {deploymentEnvironment.name}
                          </div>

                          {deploymentEnvironment.ui_kit_configuration ? (
                            <>
                              <div>
                                Published Version:{" "}
                                {
                                  deploymentEnvironment.ui_kit_configuration
                                    .version
                                }
                              </div>
                              <div className="text-xs">
                                <div className="my-2">
                                  <div>Embed code:</div>
                                  <div>
                                    <code class="block whitespace-pre overflow-x-scroll w-full bg-slate-200 p-2">
                                      &lt;link
                                      href="https://fonts.googleapis.com/css2?family=
                                      {
                                        deploymentEnvironment
                                          .ui_kit_configuration.font_family
                                      }
                                      :ital,wght@0,100..900;1,100..900&display=swap"
                                      rel="stylesheet"&gt;
                                      <br />
                                      &lt;link href="
                                      {rootUrl}/embed/
                                      {deploymentEnvironment.embed_key}
                                      .css" rel="stylesheet" type="text/css"&gt;
                                    </code>
                                  </div>
                                </div>
                                <div>
                                  Awesome! You've published!{" "}
                                  <Link
                                    href="https://bitesite.gitbook.io/groundworkui-docs/basics/using-your-ui-kit"
                                    target="_blank"
                                  >
                                    <span className="font-bold">Now what?</span>
                                  </Link>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="text-xs">
                              Your deployment environment is all set up, but you
                              have not published to it yet. When you're ready to
                              publish, click on "Publish" to publish to this
                              environment and get your Embed link.
                            </div>
                          )}
                        </div>
                        <div>
                          <Button
                            onClick={() => {
                              publish(deploymentEnvironment);
                            }}
                          >
                            Publish
                          </Button>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <div>
          <Button
            type="primary"
            onClick={() => setShowDeploymentEnvironmentFormModal(true)}
          >
            Create new Deployment Environment
          </Button>
        </div>
      </div>
      {showDeploymentEnvironmentFormModal && (
        <DeploymentEnvironmentsFormModal
          projectId={projectId}
          onClose={() => setShowDeploymentEnvironmentFormModal(false)}
          onSuccess={() => {
            loadDeploymentEnvironments();
            setShowDeploymentEnvironmentFormModal(false);
          }}
        />
      )}
    </>
  );
};

export default DeploymentEnvironmentsIndex;
